import React from "react";

import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { readOperatorAccountRequest } from "@auto/users/__OperatorAccountRequestDetailsWidget";
import { operatorRoleLabelKey, operatorScopeLabelKey, operatorTerritory } from "./operatorTerritory";
import { createQrMatrix } from "./qrCode.mjs";

// Reuse the visual language of the generated operator-account details without
// editing generated code or depending on its private, non-exported sections.
import layout from "@auto/users/__SignatureDetails.module.styl";
import styles from "./OperatorAccountRequestDetails.module.styl";

const field = (record, camelName, snakeName = camelName) =>
  record?.[camelName] ?? record?.[snakeName] ?? null;

const dateTime = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return new Date(value).toLocaleString();
  return String(value).replace("T", " ").slice(0, 19);
};

const fullName = (row) => [field(row, "firstName", "first_name"), field(row, "lastName", "last_name")]
  .filter(Boolean).join(" ");

export const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <section className={layout.card}>
    <header className={layout.cardHeader}>
      <div className={layout.cardTitleRow}>
        <div className={layout.cardIconWrap}><Icon /></div>
        <div>
          <h2 className={layout.cardTitle}>{t(title)}</h2>
          {subtitle ? <div className={layout.cardSubtitle}>{t(subtitle)}</div> : null}
        </div>
      </div>
    </header>
    <div className={layout.cardBody}>{children}</div>
  </section>
);

const Field = ({ label, value, children }) => {
  if ((value === null || value === undefined || value === "") && !children) return null;
  return (
    <div className={layout.field}>
      <div className={layout.fieldLabel}>{t(label)}</div>
      <div className={layout.fieldValue}>{children ?? String(value)}</div>
    </div>
  );
};

export const PublicKeyQr = ({ value }) => {
  const matrix = React.useMemo(() => createQrMatrix(value), [value]);
  if (!matrix) return null;

  // Consecutive dark modules are combined into one SVG path per row. A four-
  // module quiet zone and crisp black/white modules make the key scannable.
  const quiet = 4;
  const size = matrix.length + quiet * 2;
  const path = matrix.map((row, y) => {
    const runs = [];
    for (let x = 0; x < row.length;) {
      if (!row[x]) { x++; continue; }
      const start = x;
      while (x < row.length && row[x]) x++;
      runs.push(`M${start + quiet},${y + quiet}h${x - start}v1h-${x - start}z`);
    }
    return runs.join("");
  }).join("");

  return (
    <div className={styles.qrWrap}>
      <svg className={styles.qr} viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg" role="img"
        aria-label={t("operator.request.publicKeyQr")} shapeRendering="crispEdges">
        <rect width={size} height={size} fill="#fff" />
        <path d={path} fill="#000" />
      </svg>
      <div className={styles.qrLabel}>{t("operator.request.publicKeyQr")}</div>
    </div>
  );
};

export const OperatorAccountRequestDetails = ({ gui, id, canManage = false }) => {
  const [request, setRequest] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const value = await readOperatorAccountRequest(id);
      setRequest(value);
      setError(value ? "" : t("operator.request.notFound"));
    } catch (cause) {
      setError(cause?.message || String(cause));
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => { load(); }, [load]);

  const decide = async (action) => {
    if (!request?.gid || saving || !canManage) return;
    // The generated approval API explicitly accepts READY and ERROR. Never
    // suppress the action because automated territorial validation failed.
    if (!["READY", "ERROR"].includes(String(field(request, "requestStatus", "request_status") || "").toUpperCase())) return;
    setSaving(true);
    setError("");
    try {
      await rest.request(
        `${getServiceUri()}operator-account-request/${encodeURIComponent(request.gid)}/${action}`,
        "POST", {}
      );
      await load();
    } catch (cause) {
      setError(cause?.message || String(cause));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !request) return <div className={layout.loading}>{t("operator.request.loading")}</div>;
  if (!request) return <div className={layout.error}>{error || t("operator.request.notFound")}</div>;

  const requestStatus = String(field(request, "requestStatus", "request_status") || "").toUpperCase();
  const translatedStatus = t(`operator.request.status.${requestStatus}`);
  const statusLabel = translatedStatus.startsWith("[") ? requestStatus : translatedStatus;
  const canReview = canManage && ["READY", "ERROR"].includes(requestStatus);
  const scope = operatorTerritory(request);
  const key = field(request, "publicKey", "public_key");
  const createdUserGid = field(request, "createdOperatorUserGid", "created_operator_user_gid");
  const reviewerId = field(request, "reviewerBackofficeUserId", "reviewer_backoffice_user_id");
  const reason = field(request, "decisionReason", "decision_reason");

  return (
    <div className={layout.page}>
      <section className={layout.header}>
        <div className={layout.headerMain}>
          <div className={layout.heroIconWrap}><ManageAccountsOutlinedIcon className={layout.heroIcon} /></div>
          <div>
            <h1 className={layout.headerTitle}>{fullName(request) || t("operator.request.fallbackName")}</h1>
            <div className={layout.headerSubtitle}>{t("operator.request.reference")} #{request.gid || id}</div>
          </div>
        </div>
        <div className={layout.headerAside}>
          <div className={layout.statusPill}>{statusLabel}</div>
          {canReview ? (
            <div className={layout.headerActions}>
              <button type="button" className={styles.approve} disabled={saving} onClick={() => decide("approve")}>{t("operator.request.approve")}</button>
              <button type="button" className={styles.reject} disabled={saving} onClick={() => decide("reject")}>{t("operator.request.reject")}</button>
            </div>
          ) : null}
        </div>
      </section>

      {error ? <div className={layout.error} role="alert">{error}</div> : null}
      {!scope.valid ? (
        <div className={styles.notice} role="status">{t("operator.request.assignmentWarning")}</div>
      ) : null}

      <div className={layout.gridTwo}>
        <SectionCard icon={PersonOutlineOutlinedIcon} title="voter.applicant" subtitle="operator.request.identityHint">
          <div className={layout.fieldsGrid}>
            <Field label="person.firstName" value={field(request, "firstName", "first_name")} />
            <Field label="person.lastName" value={field(request, "lastName", "last_name")} />
            <Field label="user.username" value={request.username} />
            <Field label="person.nationalId" value={field(request, "nationalId", "national_id")} />
            <Field label="common.deviceId" value={field(request, "deviceId", "device_id")} />
          </div>
        </SectionCard>
        <SectionCard icon={ScheduleOutlinedIcon} title="operator.request.lifecycle" subtitle="operator.request.lifecycleHint">
          <div className={layout.fieldsGrid}>
            <Field label="common.status" value={statusLabel} />
            <Field label="voter.submittedAt" value={dateTime(field(request, "submittedAt", "submitted_at"))} />
            <Field label="voter.reviewedAt" value={dateTime(field(request, "reviewedAt", "reviewed_at"))} />
          </div>
        </SectionCard>
      </div>

      <div className={layout.gridTwo}>
        <SectionCard icon={BadgeOutlinedIcon} title="operator.request.territory" subtitle="operator.request.territoryHint">
          <div className={layout.fieldsGrid}>
            <Field label="operator.type" value={t(operatorRoleLabelKey(scope.role))} />
            <Field label={operatorScopeLabelKey(scope.role)} value={scope.territoryLabel || t("operator.scope.unassigned")} />
          </div>
        </SectionCard>
        {(createdUserGid || reviewerId || reason) ? (
          <SectionCard icon={SupervisorAccountOutlinedIcon} title="operator.request.decision" subtitle="operator.request.decisionHint">
            <div className={layout.fieldsGrid}>
              {createdUserGid ? <Field label="operator.request.createdUser">
                <button type="button" className={styles.link} onClick={() => gui.goTo(`/operator-user/${createdUserGid}`)}>
                  #{createdUserGid}
                </button>
              </Field> : null}
              <Field label="operator.request.reviewer" value={reviewerId} />
              <Field label="operator.request.decisionReason" value={reason} />
            </div>
          </SectionCard>
        ) : null}
      </div>

      {key ? (
        <SectionCard icon={VpnKeyOutlinedIcon} title="operator.request.publicKey" subtitle="operator.request.publicKeyHint">
          <PublicKeyQr value={key} />
        </SectionCard>
      ) : null}
    </div>
  );
};

export default OperatorAccountRequestDetails;
